import { Extension } from '@tiptap/core'
import type { Node } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

interface FirstMatchDecoratorOptions {
  className: string
  regex: RegExp
}

export const FirstMatchDecorator = Extension.create<FirstMatchDecoratorOptions>(
  {
    name: 'firstMatchDecorator',

    addOptions() {
      return {
        className: 'font-mono tabular-nums text-foreground',
        regex: /-?\d+(?:\.\d+)?/,
      }
    },

    addProseMirrorPlugins() {
      const regex = this.options.regex
      const className = this.options.className

      return [
        new Plugin({
          key: FIRST_MATCH_DECORATOR_PLUGIN_KEY,
          state: {
            init: (_, { doc }) => createDecorations(doc, regex, className),
            apply: (tr, oldDecorationSet, _oldState, newState) => {
              if (!tr.docChanged) {
                return oldDecorationSet
              }
              return createDecorations(newState.doc, regex, className)
            },
          },
          props: {
            decorations(state) {
              return this.getState(state)
            },
          },
        }),
      ]
    },
  }
)

export const FIRST_MATCH_DECORATOR_PLUGIN_KEY = new PluginKey(
  'firstMatchDecorator'
)

function createDecorations(
  doc: Node,
  regex: RegExp,
  className: string
): DecorationSet {
  const decorations: Decoration[] = []
  let foundFirstMatch = false

  doc.descendants((node, pos) => {
    if (foundFirstMatch) {
      return true
    }

    // Only operate on textblocks (paragraphs, headings, etc)
    if (!node.isTextblock) {
      return true
    }

    const text = node.textContent
    if (!text) {
      return true
    }

    const match = regex.exec(text)
    if (!match) {
      return true
    }

    const matchIndex = match.index
    const matchLength = match[0].length
    if (matchLength <= 0) {
      return true
    }

    const matchEnd = matchIndex + matchLength
    let textOffset = 0
    let decorated = false

    // Map from `matchIndex` in `node.textContent` back to document positions.
    node.forEach((child, offset) => {
      if (!child.isText || decorated) {
        if (child.isText) {
          const childText = child.text ?? ''
          textOffset += childText.length
        }
        return
      }

      const childText = child.text ?? ''
      const childStartInText = textOffset
      const childEndInText = childStartInText + childText.length

      // Does the match overlap this text child?
      if (matchIndex < childEndInText && matchEnd > childStartInText) {
        const startInChild = Math.max(0, matchIndex - childStartInText)
        const endInChild = Math.min(
          childText.length,
          matchEnd - childStartInText
        )

        const childDocPos = pos + 1 + offset
        const from = childDocPos + startInChild
        const to = childDocPos + endInChild

        if (from < to) {
          decorations.push(Decoration.inline(from, to, { class: className }))
          decorated = true
          foundFirstMatch = true
        }
      }

      textOffset += childText.length
    })

    return true
  })

  return decorations.length > 0
    ? DecorationSet.create(doc, decorations)
    : DecorationSet.empty
}
